/* aa-header.js — drawer and sticky behaviour for the Aesthetic Avenue header (PLAN.md PG0)
 *
 * Deliberately not Dawn's MenuDrawer: that class drives <details> elements and expects
 * component-menu-drawer.css's structure. This drawer is plain elements moved by a transform,
 * which is what lets a submenu slide in over the root list as the design shows.
 *
 * No dependencies, no framework. Guards on every lookup so a missing drawer cannot throw and
 * take the rest of the page's scripts down with it.
 */
(function () {
  'use strict';

  // Force the browser to commit an element's current computed style before we change it.
  //
  // Going from `display: none` to displayed and changing `transform` in the same task gives the
  // engine a single style recalculation with no "before" value, so the transition is skipped and
  // the element simply appears in its end position. Reading a layout property in between forces
  // the pending style to flush, which is what gives the transition something to animate from.
  // requestAnimationFrame — even nested — does not reliably do this, because rAF callbacks run
  // before style and layout for the frame.
  function flushStyle(el) {
    return el.offsetWidth;
  }

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function Drawer(root) {
    this.root = root;
    this.panel = root.querySelector('.aa-drawer__panel');
    this.openers = document.querySelectorAll('[data-aa-drawer-toggle], [data-aa-drawer-open]');
    this.header = document.querySelector('aa-header');
    this.lastFocused = null;
    this.bind();
  }

  Drawer.prototype.bind = function () {
    var self = this;

    Array.prototype.forEach.call(this.openers, function (btn) {
      btn.addEventListener('click', function () {
        // The same button opens and closes, so focus never has to move to a separate control.
        if (self.root.hidden) {
          self.open(btn);
        } else {
          self.close();
        }
      });
    });

    this.root.addEventListener('click', function (event) {
      if (event.target.closest('[data-aa-drawer-close]')) {
        self.close();
        return;
      }
      var opener = event.target.closest('[data-aa-submenu-open]');
      if (opener) {
        self.openSubmenu(opener.getAttribute('data-aa-submenu-open'), opener);
        return;
      }
      if (event.target.closest('[data-aa-submenu-close]')) {
        self.closeSubmenu();
      }
    });

    window.addEventListener(
      'resize',
      function () {
        if (!self.root.hidden) self.positionUnderBar();
      },
      { passive: true }
    );

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || self.root.hidden) return;
      // Escape steps back one level before closing, which is what a drilled-in user expects.
      if (self.root.querySelector('.aa-drawer__submenu--open')) {
        self.closeSubmenu();
      } else {
        self.close();
      }
    });

    // Keep focus inside the panel while it is open.
    this.root.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || self.root.hidden) return;
      var items = self.panel.querySelectorAll(FOCUSABLE);
      var visible = Array.prototype.filter.call(items, function (el) {
        return el.offsetParent !== null;
      });
      if (!visible.length) return;
      var first = visible[0];
      var last = visible[visible.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  // The drawer hangs off the bottom of the header row, which stays visible above it. That row's
  // position depends on scroll and on the sticky state, so it is measured at open time rather
  // than hard-coded.
  Drawer.prototype.positionUnderBar = function () {
    var bar = document.querySelector('.aa-header__bar');
    var top = 0;
    if (bar) {
      top = Math.max(0, bar.getBoundingClientRect().bottom);
    }
    this.root.style.setProperty('--aa-drawer-top', top + 'px');
  };

  Drawer.prototype.open = function (trigger) {
    var self = this;
    this.lastFocused = trigger || document.activeElement;
    this.positionUnderBar();
    this.root.hidden = false;

    var root = this.root;
    flushStyle(this.panel); // commit translateY(-100%) before switching to translateY(0)
    root.classList.add('aa-drawer--open');

    // The rows start landing while the panel is still travelling, so the two read as one gesture
    // rather than a slide followed by a separate list animation.
    clearTimeout(this.revealTimer);
    this.revealTimer = setTimeout(function () {
      root.classList.add('aa-drawer--revealed');
    }, self.reducedMotion() ? 0 : 170);

    document.documentElement.classList.add('aa-drawer-locked');
    if (this.header) this.header.classList.add('aa-header--drawer-open');
    Array.prototype.forEach.call(this.openers, function (btn) {
      btn.setAttribute('aria-expanded', 'true');
      var label = btn.querySelector('[data-aa-menu-label]');
      if (label) label.textContent = self.closeLabel();
    });
    var target = this.panel.querySelector(FOCUSABLE);
    if (target) target.focus({ preventScroll: true });
  };

  // The button's accessible name has to follow its function, or a screen-reader user is told
  // "menu" on a control that now closes. Read from data attributes so the strings stay in the
  // locale files rather than here.
  Drawer.prototype.closeLabel = function () {
    return this.root.getAttribute('data-close-label') || 'Close';
  };

  Drawer.prototype.openLabel = function () {
    return this.root.getAttribute('data-open-label') || 'Menu';
  };

  Drawer.prototype.reducedMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  Drawer.prototype.close = function () {
    var self = this;
    clearTimeout(this.revealTimer);
    // Drop the reveal first so the rows fade together instead of staggering out under a moving
    // panel, then start the panel's own (faster, ease-in) exit.
    this.root.classList.remove('aa-drawer--revealed');
    this.root.classList.remove('aa-drawer--open');
    document.documentElement.classList.remove('aa-drawer-locked');
    if (this.header) this.header.classList.remove('aa-header--drawer-open');
    Array.prototype.forEach.call(this.openers, function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      var label = btn.querySelector('[data-aa-menu-label]');
      if (label) label.textContent = self.openLabel();
    });

    var done = function () {
      self.root.hidden = true;
      self.closeSubmenu(true);
    };
    // Wait for the slide-out, but never hang if the transition does not fire.
    var fallback = setTimeout(done, 600);
    this.panel.addEventListener('transitionend', function handler(e) {
      if (e.target !== self.panel || e.propertyName !== 'transform') return;
      clearTimeout(fallback);
      self.panel.removeEventListener('transitionend', handler);
      done();
    });

    if (this.lastFocused) this.lastFocused.focus();
  };

  Drawer.prototype.openSubmenu = function (id, trigger) {
    var panel = this.root.querySelector('[data-aa-submenu="' + id + '"]');
    if (!panel) return;
    panel.hidden = false;
    panel.scrollTop = 0;
    flushStyle(panel); // commit translateX(100%) before switching to translateX(0)
    panel.classList.add('aa-drawer__submenu--open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var back = panel.querySelector('[data-aa-submenu-close]');
    // preventScroll matters: focusing an element inside a panel that is mid-transform makes the
    // browser scroll it into view, which fights the running transition and shows up as jitter
    // rather than a clean slide.
    if (back) back.focus({ preventScroll: true });
  };

  Drawer.prototype.closeSubmenu = function (immediate) {
    var open = this.root.querySelectorAll('.aa-drawer__submenu--open');
    Array.prototype.forEach.call(open, function (panel) {
      panel.classList.remove('aa-drawer__submenu--open');
      if (immediate) {
        panel.hidden = true;
      } else {
        setTimeout(function () {
          if (!panel.classList.contains('aa-drawer__submenu--open')) panel.hidden = true;
        }, 320);
      }
    });
    var expanded = this.root.querySelectorAll('[data-aa-submenu-open][aria-expanded="true"]');
    Array.prototype.forEach.call(expanded, function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
  };

  function initSticky(header) {
    if (header.getAttribute('data-sticky') !== 'true') return;

    // Anything else pinned on the page has to stop below the header rather than slide underneath
    // it, so the header publishes how much room it is actually taking. It is the header's own
    // measurement to make: nothing else on the page knows when the bar has hidden itself, and a
    // second module reading `offsetHeight` would keep reporting the full bar while it is off
    // screen — which is what left the product page's buy card sitting 114px low for the whole
    // scroll down. Zero while hidden, so the card rides back up with it.
    var section = header.closest('.aa-header-section') || header.parentElement;
    var strip = document.querySelector('.aa-announcement-section');

    var publishHeight = function () {
      // The strip pins above the header, so the header's own `top` is the strip's height.
      var stripH =
        strip && window.getComputedStyle(strip).position === 'sticky'
          ? Math.round(strip.getBoundingClientRect().height)
          : 0;
      document.documentElement.style.setProperty('--aa-announcement-h', stripH + 'px');

      var pinned =
        section &&
        window.getComputedStyle(section).position === 'sticky' &&
        !header.classList.contains('aa-header--hidden');

      // Everything else pinned on the page has to clear the WHOLE chrome, not just the header:
      // the strip stays put even while the header hides itself on the way down.
      document.documentElement.style.setProperty(
        '--aa-header-h',
        (stripH + (pinned ? Math.round(section.getBoundingClientRect().height) : 0)) + 'px'
      );
    };

    var lastY = window.scrollY;
    // Hiding on the way down is a phone behaviour. From 990px the header simply stays pinned
    // (owner's call, 2026-10-05): a desktop window has the height to keep it, and a header that
    // slides away and back made the pinned collection sidebar move with it.
    var wide = window.matchMedia('(min-width: 990px)');

    // THE HEADER CAN CHANGE ITS OWN HEIGHT — the search band opens inside it. Anything that grows
    // above the viewport makes the browser add the same amount to scrollY so the page stays
    // visually still (scroll anchoring), and that adjustment is NOT the buyer scrolling. Without
    // this, opening the band at scrollY 820 jumped it to 889 and the 69px read as a scroll down
    // past the 200px threshold, so the header hid itself the instant search was opened.
    // The window is short, so a real scroll a moment later still hides the header as usual.
    var settleUntil = 0;
    header.aaSettleScroll = function (ms) {
      lastY = window.scrollY;
      settleUntil = Date.now() + (ms || 250);
    };

    var onScroll = function () {
      var y = window.scrollY;
      var drawer = document.querySelector('[data-aa-drawer]');
      if (drawer && !drawer.hidden) return; // don't move the header out from under an open drawer

      // Absorb our own reflow. `stuck` and `publishHeight` still run — skipping them would leave
      // --aa-header-h stale for everything else pinned on the page.
      if (Date.now() < settleUntil) {
        lastY = y;
        header.classList.toggle('aa-header--stuck', y > 0);
        publishHeight();
        return;
      }

      header.classList.toggle('aa-header--stuck', y > 0);
      // On the phone: hide on the way down, reveal on the way up — reordering buyers scroll long
      // listings. On desktop it never hides.
      if (!wide.matches && y > 200 && y > lastY) {
        header.classList.add('aa-header--hidden');
      } else {
        header.classList.remove('aa-header--hidden');
      }
      lastY = y;
      publishHeight();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', publishHeight, { passive: true });
    // Widened past 990px while hidden: bring it back now rather than on the next scroll up.
    var onWide = function () {
      if (wide.matches) header.classList.remove('aa-header--hidden');
      publishHeight();
    };
    if (wide.addEventListener) wide.addEventListener('change', onWide);
    else if (wide.addListener) wide.addListener(onWide);
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(publishHeight);
      if (section) ro.observe(section);
      if (strip) ro.observe(strip);
    }
    onScroll();
  }

  // ------------------------------------------------------------ search band
  //
  // The band itself needs no positioning: it lives inside the sticky header section, so it rides
  // with the header by construction (see aa-header.liquid). All this does is open and close it.
  //
  // The toggle stays a real link to the search page in the markup, so search survives with no
  // JavaScript. Binding it here is what turns it into a disclosure, which is why the ARIA is
  // added here too rather than in Liquid — announcing a collapsed region that nothing can expand
  // would be worse than announcing a link.
  function initSearchBand(header) {
    var band = document.querySelector('[data-aa-search-band]');
    var toggle = document.querySelector('[data-aa-search-toggle]');
    if (!band || !toggle) return;

    // Bound at every width, deliberately. Desktop keeps the field in the bar and CSS hides both
    // this toggle and the band there, so the disclosure cannot be reached and the ARIA below sits
    // on a `display: none` element, out of the accessibility tree. Binding only below 990px
    // instead would mean a window opened wide and then narrowed had a dead search icon, since
    // this runs once on load.
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', band.id);

    var inner = band.querySelector('.aa-header__search-band-inner');
    var input = band.querySelector('input[type="search"]');

    // The page keeps scrolling normally behind an open band (your call, 2026-09-28) and the
    // header is NOT pinned open: the band is inside the sticky header, so it simply does
    // whatever the header does — including sliding away together on the way down and coming
    // back, still open, on the way up.
    // Kept in step with --aa-search-in / --aa-search-out in aa-header.css.
    var IN = 240;
    var OUT = 180;

    var isOpen = false;
    var endTimer = 0;

    var reducedMotion = function () {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    };

    // Absorb the reflow the band causes. The height animates, so the browser's scroll-anchoring
    // correction arrives in pieces across the whole transition rather than in one jump — the
    // window has to outlast it or the tail end still reads as a scroll down.
    var settle = function (ms) {
      if (typeof header.aaSettleScroll === 'function') header.aaSettleScroll(ms);
    };

    // Hand the band back to the stylesheet once it has arrived: `auto` so it can still be
    // measured, and the clip off so the results dropdown is not cut off at the band's own height.
    var finish = function () {
      clearTimeout(endTimer);
      band.removeAttribute('data-closing');
      if (isOpen) {
        band.style.height = 'auto';
        band.classList.add('is-open');
      } else {
        band.hidden = true;
        band.style.height = '';
      }
    };

    var setOpen = function (open) {
      if (open === isOpen) return;
      isOpen = open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

      if (open) {
        band.hidden = false;
        // Only reachable by keyboard while the bar is hidden, but then the band would open off
        // screen, so bring the header back with it.
        header.classList.remove('aa-header--hidden');
      }

      if (reducedMotion()) {
        band.classList.toggle('is-open', open);
        band.style.height = open ? 'auto' : '';
        if (!open) band.hidden = true;
        settle();
      } else {
        band.classList.remove('is-open'); // clip while it moves
        if (open) {
          band.style.height = '0px';
          flushStyle(band);
          band.style.height = inner.offsetHeight + 'px';
        } else {
          band.setAttribute('data-closing', 'true');
          band.style.height = band.offsetHeight + 'px';
          flushStyle(band);
          band.style.height = '0px';
        }
        // transitionend is the normal path; this is the backstop for the cases where it never
        // fires — a zero-height transition, or the tab being hidden mid-animation — which would
        // otherwise leave the band clipped and stuck at an inline height.
        clearTimeout(endTimer);
        endTimer = setTimeout(finish, (open ? IN : OUT) + 80);
        settle((open ? IN : OUT) + 160);
      }

      // `preventScroll` because the field is inside a sticky header and therefore already in
      // view: letting the browser scroll to it is a second way to fake a scroll gesture.
      if (open && input) input.focus({ preventScroll: true });
    };

    band.addEventListener('transitionend', function (event) {
      if (event.propertyName === 'height' && event.target === band) finish();
    });

    toggle.addEventListener('click', function (event) {
      event.preventDefault(); // it is a link only for the no-JavaScript case
      setOpen(!isOpen);
    });

    // Escape closes and hands focus back, which is the disclosure contract. It deliberately does
    // NOT fire while Dawn's results dropdown is open: the first Escape there belongs to the
    // dropdown, and predictive-search.js closes it on the same key.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !isOpen) return;
      var quick = band.querySelector('predictive-search');
      if (quick && quick.hasAttribute('open')) return;
      setOpen(false);
      toggle.focus();
    });
  }

  function init() {
    var drawer = document.querySelector('[data-aa-drawer]');
    if (drawer && !drawer.dataset.aaBound) {
      drawer.dataset.aaBound = 'true';
      new Drawer(drawer);
    }
    var header = document.querySelector('aa-header');
    if (header && !header.dataset.aaBound) {
      header.dataset.aaBound = 'true';
      initSticky(header);
      initSearchBand(header);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();
